import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [activeNav, setActiveNav] = useState('home');
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [showKakaoTooltip, setShowKakaoTooltip] = useState(false);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveNav(sectionId);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Convert FormData to URL-encoded format
    const urlEncodedData = new URLSearchParams();
    formData.forEach((value, key) => {
      urlEncodedData.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d63mplqbdoed7v0v8qhg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 3000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/8d0e19d0-7bfb-4b2d-af00-cea6f100e1c2.png" 
                alt="봉구 투어 로고" 
                className="h-12 w-auto"
              />
              <span className="text-2xl font-bold text-gray-900">봉구 투어</span>
            </div>
            
            <div className="flex items-center gap-8">
              <button onClick={() => scrollToSection('tours')} className="text-sm font-medium text-gray-700 hover:text-[#00BCD4] transition-colors cursor-pointer whitespace-nowrap">
                섬별 일정
              </button>
              <button onClick={() => scrollToSection('pricing')} className="text-sm font-medium text-gray-700 hover:text-[#00BCD4] transition-colors cursor-pointer whitespace-nowrap">
                가격
              </button>
              <button onClick={() => scrollToSection('reviews')} className="text-sm font-medium text-gray-700 hover:text-[#00BCD4] transition-colors cursor-pointer whitespace-nowrap">
                고객 후기
              </button>
              <button onClick={() => scrollToSection('booking')} className="text-sm font-medium text-gray-700 hover:text-[#00BCD4] transition-colors cursor-pointer whitespace-nowrap">
                예약
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-sm font-medium text-gray-700 hover:text-[#00BCD4] transition-colors cursor-pointer whitespace-nowrap">
                연락처
              </button>
              <button onClick={() => scrollToSection('booking')} className="px-6 py-2.5 bg-[#1A237E] text-white text-sm font-semibold rounded-full hover:bg-[#0D47A1] transition-colors cursor-pointer whitespace-nowrap">
                예약하기
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-24 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-12">
            <div className="w-2/5">
              <h1 className="text-5xl font-bold text-gray-900 leading-tight mb-6">
                세부의 숨겨진 <em className="text-[#00BCD4] not-italic">천국</em>을<br />발견하세요
              </h1>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                봉구 투어와 함께하는 프리미엄 아일랜드 호핑, 맞춤형 일정으로 당신만의 특별한 여행을 만들어드립니다
              </p>
              <div className="flex gap-4">
                <button onClick={() => scrollToSection('tours')} className="px-8 py-3.5 bg-[#00BCD4] text-white font-semibold rounded-lg hover:bg-[#00ACC1] transition-colors cursor-pointer whitespace-nowrap">
                  인기 투어 보기
                </button>
                <button onClick={() => scrollToSection('contact')} className="px-8 py-3.5 bg-white text-[#00BCD4] font-semibold rounded-lg border-2 border-[#00BCD4] hover:bg-[#00BCD4] hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                  상담 신청
                </button>
              </div>
            </div>
            
            <div className="w-3/5 relative">
              <div className="relative rounded-3xl overflow-hidden">
                <img 
                  src="https://public.readdy.ai/ai/img_res/11b9050404e1ac7ae589fcbe663ddbd4.jpg" 
                  alt="세부 해변" 
                  className="w-full h-[500px] object-cover"
                />
              </div>
              <div className="absolute bottom-8 right-8 bg-white rounded-2xl shadow-xl px-6 py-4">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">⭐</span>
                  <div>
                    <div className="text-xl font-bold text-gray-900">4.9/5.0 평점</div>
                    <div className="text-sm text-gray-600">2,500+ 만족한 여행객</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Tours */}
      <section id="tours" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">🏝️ 베스트 아일랜드 투어</h2>
            <p className="text-lg text-gray-600">가장 인기있는 세부 투어 상품을 만나보세요</p>
          </div>

          <div className="grid grid-cols-3 gap-6 mb-20">
            {[
              {
                title: '오슬롭 고래상어 투어',
                desc: '고래상어 스노클링 + 투말록 폭포 | 8시간',
                price: '₱2,500',
                slug: 'oslob-whale-shark',
                image: 'https://readdy.ai/api/search-image?query=majestic%20whale%20shark%20swimming%20underwater%20in%20clear%20blue%20ocean%20water%20oslob%20cebu%20philippines%20snorkeling%20experience%20gentle%20giant%20marine%20life%20tropical%20sea%20simple%20clean%20background&width=500&height=700&seq=tour1&orientation=portrait'
              },
              {
                title: '카와산 폭포 투어',
                desc: '캐녀닝 + 에메랄드 폭포 수영 | 6시간',
                price: '₱2,200',
                slug: 'kawasan-falls',
                image: 'https://readdy.ai/api/search-image?query=beautiful%20turquoise%20emerald%20green%20kawasan%20waterfall%20cascading%20into%20crystal%20clear%20pool%20tropical%20jungle%20cebu%20philippines%20swimming%20spot%20lush%20vegetation%20simple%20clean%20background&width=500&height=700&seq=tour2&orientation=portrait'
              },
              {
                title: '나루수안 아일랜드',
                desc: '스노클링 + 해양보호구역 | 7시간',
                price: '₱1,800',
                slug: 'nalusuan-island',
                image: 'https://readdy.ai/api/search-image?query=pristine%20white%20sand%20beach%20with%20crystal%20clear%20turquoise%20water%20nalusuan%20island%20cebu%20philippines%20tropical%20paradise%20palm%20trees%20coral%20reef%20snorkeling%20spot%20simple%20clean%20background&width=500&height=700&seq=tour3&orientation=portrait'
              }
            ].map((tour, idx) => (
              <Link key={idx} to={`/tour/${tour.slug}`} className="group relative rounded-2xl overflow-hidden cursor-pointer h-[450px] block transform transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl">
                <img src={tour.image} alt={tour.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent transition-opacity duration-300 group-hover:from-black/80"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform transition-transform duration-300 group-hover:translate-y-[-8px]">
                  <h3 className="text-2xl font-bold mb-2">{tour.title}</h3>
                  <p className="text-sm text-white/90 mb-4">{tour.desc}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-bold">{tour.price}~</span>
                    <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium group-hover:bg-[#00BCD4] transition-colors duration-300 whitespace-nowrap">
                      자세히 보기
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* All Tour Packages */}
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900">전체 투어 패키지</h3>
          </div>

          <div className="grid grid-cols-4 gap-6">
            {[
              { name: '보홀 당일 투어', desc: '초콜릿 힐 + 안경원숭이', price: '₱3,200', badge: 'BEST', slug: 'bohol-day-tour', image: 'https://readdy.ai/api/search-image?query=iconic%20chocolate%20hills%20bohol%20philippines%20hundreds%20of%20cone%20shaped%20hills%20covered%20in%20green%20grass%20unique%20geological%20formation%20panoramic%20view%20simple%20light%20background&width=400&height=400&seq=pkg1&orientation=squarish' },
              { name: '모알보알 스노클링', desc: '정어리 떼 + 거북이', price: '₱2,800', badge: '', slug: 'moalboal-snorkeling', image: 'https://readdy.ai/api/search-image?query=massive%20school%20of%20sardines%20swimming%20underwater%20moalboal%20cebu%20philippines%20snorkeling%20experience%20thousands%20of%20silver%20fish%20clear%20blue%20water%20simple%20light%20background&width=400&height=400&seq=pkg2&orientation=squarish' },
              { name: '시마라 섬 투어', desc: '무인도 + 비치 BBQ', price: '₱2,000', badge: '', slug: '', image: 'https://readdy.ai/api/search-image?query=pristine%20uninhabited%20tropical%20island%20with%20white%20sand%20beach%20crystal%20clear%20turquoise%20water%20sumilon%20island%20cebu%20philippines%20paradise%20getaway%20simple%20light%20background&width=400&height=400&seq=pkg3&orientation=squarish' },
              { name: '세부 시티 투어', desc: '역사 유적지 + 쇼핑', price: '₱1,500', badge: '', slug: '', image: 'https://readdy.ai/api/search-image?query=historic%20magellan%20cross%20cebu%20city%20philippines%20famous%20landmark%20colonial%20architecture%20cultural%20heritage%20site%20tourist%20attraction%20simple%20light%20background&width=400&height=400&seq=pkg4&orientation=squarish' },
              { name: '카모테스 섬', desc: '동굴 탐험 + 해변', price: '₱2,400', badge: '', slug: '', image: 'https://readdy.ai/api/search-image?query=mysterious%20limestone%20cave%20entrance%20with%20crystal%20clear%20blue%20water%20pool%20camotes%20island%20cebu%20philippines%20cave%20exploration%20adventure%20simple%20light%20background&width=400&height=400&seq=pkg5&orientation=squarish' },
              { name: '다이빙 체험', desc: '초보자 환영 + 강사 동행', price: '₱3,500', badge: '', slug: '', image: 'https://readdy.ai/api/search-image?query=scuba%20diver%20exploring%20colorful%20coral%20reef%20underwater%20cebu%20philippines%20tropical%20fish%20diving%20experience%20clear%20blue%20ocean%20simple%20light%20background&width=400&height=400&seq=pkg6&orientation=squarish' },
              { name: '선셋 크루즈', desc: '저녁 식사 + 라이브 음악', price: '₱2,600', badge: '', slug: 'sunset-cruise', image: 'https://readdy.ai/api/search-image?query=beautiful%20golden%20sunset%20over%20calm%20ocean%20water%20with%20silhouette%20of%20cruise%20boat%20cebu%20philippines%20romantic%20evening%20sky%20orange%20pink%20colors%20simple%20light%20background&width=400&height=400&seq=pkg7&orientation=squarish' },
              { name: '맹그로브 카약', desc: '생태 투어 + 조류 관찰', price: '₱1,900', badge: '', slug: '', image: 'https://readdy.ai/api/search-image?query=peaceful%20mangrove%20forest%20with%20kayak%20on%20calm%20green%20water%20cebu%20philippines%20eco%20tourism%20lush%20tropical%20vegetation%20nature%20experience%20simple%20light%20background&width=400&height=400&seq=pkg8&orientation=squarish' }
            ].map((pkg, idx) => (
              <div key={idx} className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100 transform hover:scale-[1.03] hover:-translate-y-2">
                {pkg.badge && (
                  <div className="absolute top-4 left-4 z-10">
                    <span className="px-3 py-1 bg-[#1A237E] text-white text-xs font-bold rounded-full whitespace-nowrap">{pkg.badge}</span>
                  </div>
                )}
                <div className="relative p-4">
                  <div className="w-full h-48 bg-gray-50 rounded-xl overflow-hidden mb-4">
                    <img src={pkg.image} alt={pkg.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 text-center mb-2 transition-colors duration-300 group-hover:text-[#00BCD4]">{pkg.name}</h4>
                  <p className="text-sm text-gray-600 text-center mb-3">{pkg.desc}</p>
                  <div className="text-center">
                    <span className="text-xl font-bold text-[#00BCD4]">{pkg.price}부터</span>
                  </div>
                  <div className="text-right mt-3">
                    {pkg.slug ? (
                      <Link to={`/tour/${pkg.slug}`} className="text-sm text-[#00BCD4] hover:text-[#00ACC1] font-medium cursor-pointer whitespace-nowrap inline-flex items-center gap-1 transition-all duration-300 group-hover:gap-2">
                        상세보기 <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
                      </Link>
                    ) : (
                      <button className="text-sm text-[#00BCD4] hover:text-[#00ACC1] font-medium cursor-pointer whitespace-nowrap inline-flex items-center gap-1 transition-all duration-300 group-hover:gap-2">
                        상세보기 <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">투명한 가격 정책</h2>
            <p className="text-lg text-gray-600">모든 비용이 포함된 합리적인 가격</p>
          </div>

          <div className="grid grid-cols-3 gap-8">
            {[
              {
                title: '기본 패키지',
                price: '₱1,500',
                features: ['1개 섬 투어', '점심 식사 포함', '스노클링 장비', '가이드 동행', '왕복 교통편']
              },
              {
                title: '프리미엄 패키지',
                price: '₱2,800',
                features: ['2-3개 섬 투어', '점심 + 간식 포함', '스노클링 + 다이빙', '전문 가이드', '호텔 픽업/드롭', '수중 사진 촬영'],
                popular: true
              },
              {
                title: 'VIP 패키지',
                price: '₱4,500',
                features: ['맞춤형 일정', '풀코스 식사', '모든 액티비티', '전담 가이드', '프라이빗 차량', '드론 촬영', 'GoPro 대여']
              }
            ].map((plan, idx) => (
              <div key={idx} className={`relative rounded-2xl p-8 ${plan.popular ? 'bg-[#00BCD4] text-white shadow-2xl scale-105' : 'bg-gray-50 text-gray-900'}`}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 bg-[#FF6B6B] text-white text-sm font-bold rounded-full whitespace-nowrap">인기</span>
                  </div>
                )}
                <h3 className="text-2xl font-bold mb-4">{plan.title}</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  <span className={`text-lg ${plan.popular ? 'text-white/80' : 'text-gray-600'}`}> / 1인</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, fidx) => (
                    <li key={fidx} className="flex items-center gap-2">
                      <i className={`ri-check-line text-xl ${plan.popular ? 'text-white' : 'text-[#00BCD4]'}`}></i>
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button className={`w-full py-3 rounded-lg font-semibold transition-colors whitespace-nowrap ${plan.popular ? 'bg-white text-[#00BCD4] hover:bg-gray-100' : 'bg-[#00BCD4] text-white hover:bg-[#00ACC1]'}`}>
                  예약하기
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="w-3 h-3 bg-[#1A237E] rotate-45"></span>
              <span className="text-sm font-bold text-[#1A237E] tracking-wider">REVIEWS</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900">여행객들의 생생한 후기</h2>
          </div>

          <div className="grid grid-cols-3 gap-6 mb-6">
            <div className="bg-[#E0F7FA] rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-[#00BCD4] rounded-full flex items-center justify-center text-white font-bold">
                  김
                </div>
                <div>
                  <div className="font-bold text-gray-900">김민지</div>
                  <div className="text-sm text-gray-600">직장인</div>
                </div>
              </div>
              <div className="text-4xl text-[#00BCD4] mb-3">"</div>
              <p className="text-gray-700 leading-relaxed">
                봉구 투어 덕분에 최고의 세부 여행을 했어요! 가이드님이 친절하고 전문적이어서 안심하고 즐길 수 있었습니다. 고래상어 투어는 정말 잊을 수 없는 경험이었어요.
              </p>
              <div className="flex gap-1 mt-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#FFB300]"></i>
                ))}
              </div>
            </div>

            <div className="bg-[#E0F7FA] rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-[#00BCD4] rounded-full flex items-center justify-center text-white font-bold">
                  박
                </div>
                <div>
                  <div className="font-bold text-gray-900">박준호</div>
                  <div className="text-sm text-gray-600">사업가</div>
                </div>
              </div>
              <div className="text-4xl text-[#00BCD4] mb-3">"</div>
              <p className="text-gray-700 leading-relaxed">
                가족과 함께한 아일랜드 호핑이 너무 좋았습니다. 아이들도 안전하게 스노클링을 즐겼고, 점심 식사도 맛있었어요. 다음에 또 이용하고 싶습니다!
              </p>
              <div className="flex gap-1 mt-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#FFB300]"></i>
                ))}
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden h-64">
              <img 
                src="https://readdy.ai/api/search-image?query=happy%20asian%20female%20tourist%20snorkeling%20with%20colorful%20tropical%20fish%20underwater%20cebu%20philippines%20smiling%20woman%20wearing%20snorkel%20mask%20clear%20blue%20water%20simple%20background&width=400&height=500&seq=review1&orientation=portrait" 
                alt="고객 후기" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div className="absolute bottom-6 left-6 text-white">
                <div className="font-bold text-lg">이수진</div>
                <div className="text-sm text-white/90">나루수안 투어</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2 relative rounded-2xl overflow-hidden h-80">
              <img 
                src="https://readdy.ai/api/search-image?query=group%20of%20happy%20asian%20tourists%20on%20white%20bangka%20boat%20enjoying%20island%20hopping%20tour%20cebu%20philippines%20friends%20smiling%20crystal%20clear%20turquoise%20water%20tropical%20paradise%20simple%20background&width=800&height=500&seq=review2&orientation=landscape" 
                alt="고객 후기" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1A237E]/80 to-transparent"></div>
              <div className="absolute bottom-8 left-8 text-white max-w-md">
                <div className="font-bold text-xl mb-2">최영수</div>
                <div className="text-sm text-white/90">보홀 당일 투어</div>
                <p className="text-white/95 leading-relaxed">
                  보홀 투어가 정말 환상적이었습니다. 초콜릿 힐의 장관과 귀여운 안경원숭이까지! 봉구 투어의 세심한 배려에 감동했어요.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-6">
              <div className="bg-[#E0F7FA] rounded-2xl p-6 flex-1">
                <div className="text-3xl text-[#00BCD4] mb-3">"</div>
                <p className="text-gray-700 text-sm leading-relaxed mb-4">
                  가격 대비 정말 만족스러운 투어였습니다. 추천합니다!
                </p>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-[#00BCD4] rounded-full flex items-center justify-center text-white text-sm font-bold">
                    정
                  </div>
                  <div>
                    <div className="font-bold text-sm text-gray-900">정하늘</div>
                    <div className="text-xs text-gray-600">대학생</div>
                  </div>
                </div>
              </div>

              <div className="bg-[#E0F7FA] rounded-2xl p-6 flex-1">
                <div className="text-3xl text-[#00BCD4] mb-3">"</div>
                <p className="text-gray-700 text-sm leading-relaxed mb-4">
                  신혼여행으로 완벽했어요. 로맨틱한 선셋 크루즈 최고!
                </p>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-[#00BCD4] rounded-full flex items-center justify-center text-white text-sm font-bold">
                    강
                  </div>
                  <div>
                    <div className="font-bold text-sm text-gray-900">강서연</div>
                    <div className="text-xs text-gray-600">신혼부부</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="booking" className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl font-bold text-gray-900 mb-6">
            지금 바로 세부 여행을 시작하세요
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            24시간 예약 가능 | 무료 상담 | 최저가 보장
          </p>
          <button onClick={() => scrollToSection('contact')} className="px-10 py-4 bg-[#5D4037] text-white text-lg font-semibold rounded-full hover:bg-[#4E342E] transition-colors inline-flex items-center gap-2 cursor-pointer whitespace-nowrap">
            카카오톡 상담하기
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <div className="text-sm text-gray-500 mb-6">ABOUT US</div>
            <div className="max-w-2xl ml-auto">
              <h2 className="text-5xl font-bold text-gray-900 leading-tight mb-6">
                봉구 투어는<br />
                세부 현지 전문<br />
                여행사입니다
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                2024년부터 350명 이상의 여행객에게 안전하고 즐거운 세부 투어를 제공해왔습니다. 젊고 열정적인 팀과 함께하는 프리미엄 서비스를 경험하세요.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-5 gap-6">
            <div className="col-span-2 rounded-3xl overflow-hidden">
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20filipino%20tour%20guide%20team%20group%20photo%20wearing%20matching%20blue%20polo%20shirts%20smiling%20friendly%20staff%20cebu%20philippines%20tourism%20company%20simple%20light%20background&width=500&height=400&seq=about1&orientation=landscape" 
                alt="봉구 투어 팀" 
                className="w-full h-80 object-cover"
              />
            </div>
            <div className="col-span-3 rounded-3xl overflow-hidden">
              <img 
                src="https://readdy.ai/api/search-image?query=happy%20tourists%20enjoying%20island%20hopping%20activity%20on%20boat%20cebu%20philippines%20group%20of%20people%20having%20fun%20snorkeling%20swimming%20tropical%20adventure%20simple%20light%20background&width=700&height=400&seq=about2&orientation=landscape" 
                alt="투어 활동" 
                className="w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">문의하기</h2>
            <p className="text-lg text-gray-600">궁금하신 점이 있으시면 언제든 연락주세요</p>
          </div>

          <div className="grid grid-cols-2 gap-12 mb-12">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-6">연락처 정보</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#00BCD4] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-white text-lg"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">전화</div>
                    <div className="text-gray-600">+82 10-4496-8321</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#00BCD4] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-white text-lg"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">이메일</div>
                    <div className="text-gray-600">jnjbong9@gmail.com</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#00BCD4] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-white text-lg"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">주소</div>
                    <div className="text-gray-600">Cebu City, Philippines</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#FFE500] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-kakao-talk-fill text-[#3C1E1E] text-lg"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">카카오톡</div>
                    <div className="text-gray-600">"@bong9tour</div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-6">문의 양식</h3>
              <form className="space-y-4" data-readdy-form id="contact-form" onSubmit={handleSubmit}>
                <div>
                  <input 
                    type="text" 
                    name="name"
                    placeholder="이름" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#00BCD4] text-sm"
                    required
                  />
                </div>
                <div>
                  <input 
                    type="email" 
                    name="email"
                    placeholder="이메일" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#00BCD4] text-sm"
                    required
                  />
                </div>
                <div>
                  <input 
                    type="tel" 
                    name="phone"
                    placeholder="연락처" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#00BCD4] text-sm"
                    required
                  />
                </div>
                <div>
                  <textarea 
                    name="message"
                    placeholder="문의 내용" 
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#00BCD4] text-sm resize-none"
                    required
                  ></textarea>
                  <div className="text-xs text-gray-500 mt-1 text-right">최대 500자</div>
                </div>
                <button 
                  type="submit" 
                  disabled={formStatus === 'submitting'}
                  className="w-full py-3 bg-[#00BCD4] text-white font-semibold rounded-lg hover:bg-[#00ACC1] transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formStatus === 'submitting' ? '전송 중...' : '문의하기'}
                </button>
                {formStatus === 'success' && (
                  <div className="text-center text-green-600 text-sm font-medium">
                    문의가 성공적으로 전송되었습니다!
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="text-center text-red-600 text-sm font-medium">
                    전송 중 오류가 발생했습니다. 다시 시도해주세요.
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F5F5F5] py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-3xl p-12">
            <div className="grid grid-cols-5 gap-12 mb-12">
              <div className="col-span-1">
                <div className="flex items-center gap-2 mb-4">
                  <img 
                    src="https://public.readdy.ai/ai/img_res/8d0e19d0-7bfb-4b2d-af00-cea6f100e1c2.png" 
                    alt="봉구 투어" 
                    className="h-10 w-auto"
                  />
                  <span className="text-xl font-bold text-gray-900">봉구 투어</span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  필리핀 세부 현지 여행 전문<br />안전한 투어 보장
                </p>
              </div>

              <div>
                <h4 className="font-bold text-gray-900 mb-4">TOURS</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li><a href="#" className="hover:text-[#00BCD4] transition-colors cursor-pointer">아일랜드 호핑</a></li>
                  <li><a href="#" className="hover:text-[#00BCD4] transition-colors cursor-pointer">고래상어 투어</a></li>
                  <li><a href="#" className="hover:text-[#00BCD4] transition-colors cursor-pointer">다이빙 투어</a></li>
                  <li><a href="#" className="hover:text-[#00BCD4] transition-colors cursor-pointer">시티 투어</a></li>
                </ul>
              </div>

              <div>
                <h4 className="font-bold text-gray-900 mb-4">COMPANY</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li><Link to="/about" className="hover:text-[#00BCD4] transition-colors cursor-pointer">회사 소개</Link></li>
                  <li><a href="#" className="hover:text-[#00BCD4] transition-colors cursor-pointer">가이드 소개</a></li>
                  <li><a href="#" className="hover:text-[#00BCD4] transition-colors cursor-pointer">파트너십</a></li>
                  <li><a href="#" className="hover:text-[#00BCD4] transition-colors cursor-pointer">채용 정보</a></li>
                </ul>
              </div>

              <div>
                <h4 className="font-bold text-gray-900 mb-4">SUPPORT</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li><Link to="/faq" className="hover:text-[#00BCD4] transition-colors cursor-pointer">자주 묻는 질문</Link></li>
                  <li><a href="#" className="hover:text-[#00BCD4] transition-colors cursor-pointer">예약 문의</a></li>
                  <li><Link to="/refund-policy" className="hover:text-[#00BCD4] transition-colors cursor-pointer">환불 정책</Link></li>
                  <li><a href="#" className="hover:text-[#00BCD4] transition-colors cursor-pointer">이용 약관</a></li>
                </ul>
              </div>

              <div>
                <h4 className="font-bold text-gray-900 mb-4">FOLLOW US</h4>
                <div className="flex gap-3">
                  <a href="#" className="w-10 h-10 bg-[#FFE500] rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer">
                    <i className="ri-kakao-talk-fill text-[#3C1E1E] text-xl"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#515BD4] rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer">
                    <i className="ri-instagram-line text-white text-xl"></i>
                  </a>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-8 flex items-center justify-between">
              <div className="flex items-center gap-6 text-sm text-gray-600">
                <span>© 2025 봉구 투어. All rights reserved.</span>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#00BCD4] transition-colors cursor-pointer">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Kakao Talk Floating Button */}
      <div className="fixed bottom-28 right-6 z-40">
        <div className="relative">
          {showKakaoTooltip && (
            <div className="absolute bottom-full right-0 mb-2 whitespace-nowrap bg-gray-900 text-white text-sm px-3 py-2 rounded-lg shadow-lg animate-fade-in">
              카카오톡으로 빠른 상담
              <div className="absolute bottom-0 right-4 translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900"></div>
            </div>
          )}
          <a
            href="https://open.kakao.com/o/sPY4m6fi"
            target="_blank"
            rel="noopener noreferrer"
            onMouseEnter={() => setShowKakaoTooltip(true)}
            onMouseLeave={() => setShowKakaoTooltip(false)}
            className="flex items-center justify-center w-14 h-14 bg-[#FEE500] rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 cursor-pointer group"
          >
            <i className="ri-kakao-talk-fill text-[#3C1E1E] text-2xl group-hover:scale-110 transition-transform"></i>
          </a>
        </div>
      </div>
    </div>
  );
}